import { X } from 'lucide-react';

const currency = (value: number) => `৳${Number(value || 0).toLocaleString()}`;
const round2 = (value: number) => Number((value || 0).toFixed(2));

/** One test line contributing to the referral commission. */
export interface ReferralCalculationLine {
  /** Test name, shown as the row heading. */
  name: string;
  /** Quantity billed for this test. */
  qty: number;
  /** Gross amount (price × qty) before discount. */
  gross: number;
  /** Per-line discount percentage applied to the gross. */
  discountPercent: number;
  /** Discount amount for the line. */
  discount: number;
  /** Referral commission earned on this line. */
  share: number;
}

/** Everything the referral-calculation dialog needs to render. */
export interface ReferralCalculationDetails {
  /** Gross bill amount (before any discount). */
  gross: number;
  /** Effective referral percentage (receipt override or partner default). */
  percent: number;
  /** Per-test breakdown that produces the total commission. */
  lines: ReferralCalculationLine[];
  /** Sum of every line's discount amount. */
  totalDiscount: number;
  /** Total referral commission (sum of each line's share). */
  totalShare: number;
  /** Amount retained by the hospital (gross − total discount). */
  hospitalShare: number;
}

/**
 * Compact dialog explaining how the referral commission was calculated:
 * the PER-TEST discount × the referral percentage.
 */
export function ReferralCalculationModal({
  open,
  details,
  onClose,
}: {
  open: boolean;
  details: ReferralCalculationDetails;
  onClose: () => void;
}) {
  if (!open) return null;

  const { gross, percent, lines, totalShare } = details;
  const referralPercentOfBill =
    gross > 0 ? Number(((totalShare / gross) * 100).toFixed(2)) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-sm font-bold text-text">Referral calculation</h3>
            <p className="text-xs text-text-muted mt-0.5">
              {percent}% of the test bill
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-2 text-sm">
          {lines.map((line, index) => (
            <div key={`${line.name}-${index}`} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-text truncate">
                  {line.name}
                  <span className="text-text-muted"> × {line.qty}</span>
                </p>
                <p className="text-[0.6875rem] text-text-muted">
                  Gross {currency(round2(line.gross))} × {percent}%
                </p>
              </div>
              <span className="text-primary font-medium shrink-0">
                {currency(Math.round(line.share))}
              </span>
            </div>
          ))}

          <p className="text-[0.6875rem] text-text-muted">
            Test gross {currency(round2(gross))} × {percent}% ={' '}
            <span className="font-semibold text-primary">{currency(round2(totalShare))}</span>{' '}
            ({referralPercentOfBill}% of bill)
          </p>

          <div className="h-px bg-border" />
          <div className="flex items-center justify-between font-bold">
            <span className="text-text">Referral total</span>
            <span className="text-primary">{currency(Math.round(totalShare))}</span>
          </div>
        </div>

        <div className="flex justify-end p-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
